"use client";

import Image from "next/image";
import { NumberIncrementer } from "@/components/number-incrementer";
import { Headline3, Headline6, Caption } from "@/components/typography";
import { usePortalStats, formatPortalNumber } from "@/hooks/usePortalStats";

export function StatsSection() {
  const { totalUsers, totalTasksDone, isLoading } = usePortalStats();

  //  skeleton loader while loading
  if (isLoading) {
    return (
      <section
        className="relative w-full overflow-hidden py-16 md:py-24"
        style={{ backgroundColor: "#FBEBEA" }}
      >
        <div className="absolute inset-0">
          <Image
            src="/assets/landing/stats-bg.png"
            alt="Background pattern"
            fill
            className="object-cover"
            priority
          />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12 md:mb-20">
            <div className="h-12 md:h-14 bg-spicenet-black/10 rounded-lg animate-pulse max-w-2xl mx-auto mb-3 md:mb-4" />
            <div className="h-4 md:h-5 bg-spicenet-black/10 rounded animate-pulse max-w-md mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="mb-4">
                <div className="h-20 md:h-28 lg:h-32 bg-spicenet-black/10 rounded-lg animate-pulse max-w-xs mx-auto" />
              </div>
              <div className="h-5 md:h-6 bg-spicenet-primary/20 rounded animate-pulse max-w-[150px] mx-auto" />
            </div>

            <div className="text-center">
              <div className="mb-4">
                <div className="h-20 md:h-28 lg:h-32 bg-spicenet-black/10 rounded-lg animate-pulse max-w-xs mx-auto" />
              </div>
              <div className="h-5 md:h-6 bg-spicenet-primary/20 rounded animate-pulse max-w-[180px] mx-auto" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative w-full overflow-hidden py-16 md:py-24"
      style={{ backgroundColor: "#FBEBEA" }}
    >
      <div className="absolute inset-0 opacity-30">
        <Image
          src="/assets/landing/stats-bg.png"
          alt="Background pattern"
          fill
          className="object-cover"
          priority
        />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12 md:mb-20">
          <Headline3 className="text-3xl md:text-5xl font-normal tracking-[-0.03em] text-spicenet-primary mb-3 md:mb-4">
            The Spicenet Portal in numbers
          </Headline3>
          <Caption className="text-xs md:text-sm uppercase text-spicenet-black/70 tracking-wider">
            Join the community and contribute to
            <br className="md:hidden" />
            <span className="hidden md:inline"> </span>the portal numbers
          </Caption>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-4xl mx-auto">
          {/* Total Users Stat */}
          <div className="text-center">
            <div className="mb-4">
              <NumberIncrementer
                value={totalUsers}
                className="text-6xl md:text-8xl lg:text-9xl font-bold tracking-[-0.04em] text-spicenet-black"
                duration={2000}
                formatNumber={(num) => formatPortalNumber(num)}
              />
            </div>
            <Headline6 className="text-sm md:text-base uppercase text-spicenet-primary tracking-wider">
              Total Users
            </Headline6>
          </div>

          <div className="text-center">
            <div className="mb-4">
              <NumberIncrementer
                value={totalTasksDone}
                className="text-6xl md:text-8xl lg:text-9xl font-bold tracking-[-0.04em] text-spicenet-black"
                duration={2000}
                formatNumber={(num) => formatPortalNumber(num)}
              />
            </div>
            <Headline6 className="text-sm md:text-base uppercase text-spicenet-primary tracking-wider">
              Total Tasks Done
            </Headline6>
          </div>
        </div>
      </div>
    </section>
  );
}
